import { Col, Row } from "antd";
import dayjs from "dayjs";
import { get } from "lodash";
import { useTranslation } from "react-i18next";
import {
  MdOutlineBadge,
  MdOutlineCake,
  MdOutlineSubtitles,
  MdWorkOutline,
} from "react-icons/md";
import Form from "shared/components/Form";
import FormField from "shared/components/FormField";
import Input from "shared/components/Input";
import Select from "shared/components/Select";
import { NAME_SPACES } from "shared/locales/constants";
import {
  DATE_FORMAT,
  GENDER,
  JOB_LIST,
  RELATIONSHIP,
  SUBSCRIPTION_FORM_KEYS,
} from "utils/constants";

const Partner = ({ setPercent, isFieldRequired, bindField, initialValues }) => {
  const { t } = useTranslation(NAME_SPACES.PRIVATE.BROKER.CONTRACT);
  const SUBSCRIPTION = t("SUBSCRIPTION", { returnObjects: true });
  const birthDate = get(
    initialValues,
    "spouse.identity.user.birthDate",
    undefined,
  );

  const birthNamePath = "spouse.identity.user.birthName";
  const birthNameField = bindField(birthNamePath, {
    label: SUBSCRIPTION.FORM.BIRTH_NAME.LABEL,
    required: isFieldRequired(birthNamePath),
    onBlur: () => setPercent(initialValues),
  });

  const lastnamePath = "spouse.identity.user.lastname";
  const lastnameField = bindField(lastnamePath, {
    label: SUBSCRIPTION.FORM.LAST_NAME.LABEL,
    required: isFieldRequired(lastnamePath),
    onBlur: () => setPercent(initialValues),
  });

  const firstnamePath = "spouse.identity.user.firstname";
  const firstnameField = bindField(firstnamePath, {
    label: SUBSCRIPTION.FORM.FIRST_NAME.LABEL,
    required: isFieldRequired(firstnamePath),
    onBlur: () => setPercent(initialValues),
  });

  const professionPath = "spouse.identity.profession";
  const professionField = bindField(professionPath, {
    label: SUBSCRIPTION.FORM.PROFESSION.LABEL,
    required: isFieldRequired(professionPath),
    onBlur: () => setPercent(initialValues),
  });

  const genderPath = "spouse.identity.user.gender";
  const genderField = bindField(genderPath, {
    label: SUBSCRIPTION.FORM.GENDER.LABEL,
    required: isFieldRequired(genderPath),
    onBlur: () => setPercent(initialValues),
  });

  const socialSecurityNumberPath = "spouse.identity.socialSecurityNumber";
  const socialSecurityNumberField = bindField(socialSecurityNumberPath, {
    label: SUBSCRIPTION.FORM.SOCIAL_SECURITY_NUMBER.LABEL,
    required: isFieldRequired(socialSecurityNumberPath),
    onBlur: () => setPercent(initialValues),
  });

  return (
    get(initialValues, "project.contact.relatedPersons", []).find(
      (person) => person.relationship === RELATIONSHIP.SPOUSE,
    ) && (
      <div
        className="subscription-form--wrapper"
        id={`${SUBSCRIPTION_FORM_KEYS.ADDITIONAL_INFORMATION.PARTNER}-section`}
      >
        <Form
          type="vertical"
          title={SUBSCRIPTION.FORM.ADDITIONAL_INFORMATION.PARTNER.TITLE}
          subtitle={SUBSCRIPTION.FORM.ADDITIONAL_INFORMATION.PARTNER.SUBTITLE}
        >
          <Row gutter={[16, 16]}>
            <Col lg={24} xs={24}>
              <FormField
                path={birthNameField.path}
                label={birthNameField.label}
                required={birthNameField.required}
                disabled={birthNameField.disabled}
              >
                {(field) => (
                  <Input
                    placeholder={SUBSCRIPTION.FORM.BIRTH_NAME.PLACEHOLDER}
                    label={birthNameField.label}
                    icon={<MdOutlineBadge size={16} />}
                    type="custom"
                    normalizeText={true}
                    defaultValue={birthNameField.defaultValue}
                    {...field}
                    onChange={birthNameField.onChange}
                    onBlur={birthNameField.onBlur}
                    optional={!birthNameField.required}
                  />
                )}
              </FormField>
            </Col>
            <Col lg={24} xs={24}>
              <FormField
                path={lastnameField.path}
                label={lastnameField.label}
                required={lastnameField.required}
                disabled={lastnameField.disabled}
              >
                {(field) => (
                  <Input
                    placeholder={SUBSCRIPTION.FORM.LAST_NAME.PLACEHOLDER}
                    label={lastnameField.label}
                    icon={<MdOutlineBadge size={16} />}
                    type="custom"
                    normalizeText={true}
                    defaultValue={lastnameField.defaultValue}
                    {...field}
                    onChange={lastnameField.onChange}
                    onBlur={lastnameField.onBlur}
                  />
                )}
              </FormField>
            </Col>
            <Col lg={24} xs={24}>
              <FormField
                path={firstnameField.path}
                label={firstnameField.label}
                required={firstnameField.required}
                disabled={firstnameField.disabled}
              >
                {(field) => (
                  <Input
                    label={firstnameField.label}
                    icon={<MdOutlineBadge size={16} />}
                    placeholder={SUBSCRIPTION.FORM.FIRST_NAME.PLACEHOLDER}
                    type="custom"
                    normalizeText={true}
                    defaultValue={firstnameField.defaultValue}
                    {...field}
                    onChange={firstnameField.onChange}
                    onBlur={firstnameField.onBlur}
                  />
                )}
              </FormField>
            </Col>
            <Col lg={24} xs={24}>
              <FormField
                path={professionField.path}
                label={professionField.label}
                required={professionField.required}
                disabled={professionField.disabled}
              >
                {(field) => (
                  <Select
                    options={Object.entries(JOB_LIST).map(([key, value]) => ({
                      label: SUBSCRIPTION.FORM.PROFESSION.LIST[key],
                      value,
                    }))}
                    placeholder={SUBSCRIPTION.FORM.PROFESSION.PLACEHOLDER}
                    label={professionField.label}
                    icon={<MdWorkOutline size={16} />}
                    defaultValue={professionField.defaultValue}
                    {...field}
                    onChange={professionField.onChange}
                    onBlur={professionField.onBlur}
                    optional={
                      !professionField.required && SUBSCRIPTION.FORM.OPTIONAL
                    }
                  />
                )}
              </FormField>
            </Col>
            <Col lg={24} xs={24}>
              <FormField
                path={genderField.path}
                label={genderField.label}
                required={genderField.required}
                disabled={genderField.disabled}
              >
                {(field) => (
                  <Input
                    type="radio"
                    variant="group"
                    label={genderField.label}
                    values={[
                      {
                        label: SUBSCRIPTION.FORM.GENDER.OPTIONS.MR,
                        value: GENDER.MALE,
                      },
                      {
                        label: SUBSCRIPTION.FORM.GENDER.OPTIONS.MRS,
                        value: GENDER.FEMALE,
                      },
                    ]}
                    icon={<MdOutlineBadge size={16} />}
                    value={get(initialValues, genderPath)}
                    {...field}
                    onChange={genderField.onChange}
                    onBlur={genderField.onBlur}
                    optional={!genderField.required}
                  />
                )}
              </FormField>
            </Col>
            <Col lg={24} xs={24}>
              <FormField
                path={socialSecurityNumberField.path}
                label={socialSecurityNumberField.label}
                required={socialSecurityNumberField.required}
                disabled={socialSecurityNumberField.disabled}
              >
                {(field) => (
                  <Input
                    label={socialSecurityNumberField.label}
                    icon={<MdOutlineSubtitles size={16} />}
                    type="custom"
                    trim={true}
                    placeholder={
                      SUBSCRIPTION.FORM.SOCIAL_SECURITY_NUMBER.PLACEHOLDER
                    }
                    defaultValue={socialSecurityNumberField.defaultValue}
                    {...field}
                    onChange={socialSecurityNumberField.onChange}
                    onBlur={socialSecurityNumberField.onBlur}
                    optional={!socialSecurityNumberField.required}
                  />
                )}
              </FormField>
            </Col>
            <Col lg={24} xs={24}>
              <Input
                label={SUBSCRIPTION.FORM.BIRTH_DATE.LABEL}
                icon={<MdOutlineCake size={16} />}
                placeholder={SUBSCRIPTION.FORM.BIRTH_DATE.PLACEHOLDER}
                defaultValue={birthDate && dayjs(birthDate)}
                type="date"
                variant="simple"
                format={DATE_FORMAT}
                disabled
              />
            </Col>
          </Row>
        </Form>
      </div>
    )
  );
};

export default Partner;
